<script setup>
/**
 * The ticket's checklist: the steps this case needs before it can move on.
 *
 * Ticking an item is its own command server-side (completion is a timestamp with
 * an event attached, not a field edit), so the panel emits a toggle rather than
 * a save. Archiving removes the item from the list here because the endpoint
 * only ever returns active tasks — a row left behind would come back on the next
 * fetch and read as a failed delete.
 */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  tasks: {
    type: Array,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['add', 'toggle', 'archive']);

const { t } = useI18n();

const draft = ref('');

const ordered = computed(() =>
  [...props.tasks].sort((a, b) => a.position - b.position)
);

const completedCount = computed(
  () => ordered.value.filter(task => task.completed).length
);

const onAdd = () => {
  const title = draft.value.trim();
  if (!title) return;
  draft.value = '';
  emit('add', title);
};
</script>

<template>
  <section class="flex flex-col gap-3" data-testid="case-tasks-panel">
    <div class="flex items-baseline gap-2">
      <h3 class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t('SUPPORT_CASES.DETAIL.TASKS.TITLE') }}
      </h3>
      <span
        v-if="ordered.length"
        class="text-xs tabular-nums text-n-slate-11"
        data-testid="case-tasks-progress"
      >
        {{ completedCount }}/{{ ordered.length }}
      </span>
    </div>

    <p
      v-if="!ordered.length"
      class="text-sm text-n-slate-11"
      data-testid="case-tasks-empty"
    >
      {{ t('SUPPORT_CASES.DETAIL.TASKS.EMPTY') }}
    </p>

    <ul v-else class="flex flex-col gap-1">
      <li
        v-for="task in ordered"
        :key="task.id"
        class="flex items-center gap-2 group"
        :data-testid="`case-task-${task.id}`"
      >
        <Checkbox
          :model-value="task.completed"
          :disabled="disabled"
          :aria-label="task.title"
          @update:model-value="emit('toggle', { task, completed: $event })"
        />
        <span
          class="min-w-0 text-sm truncate"
          :class="
            task.completed ? 'text-n-slate-10 line-through' : 'text-n-slate-12'
          "
        >
          {{ task.title }}
        </span>
        <Button
          variant="ghost"
          color="slate"
          size="xs"
          icon="i-lucide-trash-2"
          class="ltr:ml-auto rtl:mr-auto"
          :disabled="disabled"
          :aria-label="
            t('SUPPORT_CASES.DETAIL.TASKS.ARCHIVE', { title: task.title })
          "
          :data-testid="`case-task-archive-${task.id}`"
          @click="emit('archive', task)"
        />
      </li>
    </ul>

    <div class="flex items-center gap-2">
      <Input
        v-model="draft"
        size="sm"
        class="grow"
        :disabled="disabled"
        :placeholder="t('SUPPORT_CASES.DETAIL.TASKS.PLACEHOLDER')"
        :aria-label="t('SUPPORT_CASES.DETAIL.TASKS.PLACEHOLDER')"
        data-testid="case-task-input"
        @enter="onAdd"
      />
      <Button
        variant="outline"
        color="slate"
        size="sm"
        :label="t('SUPPORT_CASES.DETAIL.TASKS.ADD')"
        :disabled="disabled || !draft.trim()"
        data-testid="case-task-add"
        @click="onAdd"
      />
    </div>
  </section>
</template>
